import React, { useEffect } from "react";
import { useState } from "react";
import { B2bCompanyProduct } from "../../../../model/B2bCompany";
import { Event } from "../../../../model/Event";
import { Product } from "../../../../model/Product";
import ProductVariantPriceGroupSelector, { IProductVariantPriceGroupSelectorResult } from "../../../Common/ProductVariantPriceGroupSelector";
import { Button, Card, CardActions, CardContent, CardHeader, Grid, MenuItem, TextField } from "@material-ui/core";
import { ApiBackend } from "../../../../providers/apibackend";
import { ProductVariant } from "../../../../model/ProductVariant";
import { CompanyReservationCode } from "../../../../model/CompanyReservationCode";
import ReservationCodeSelector from "./ReservationCodeSelector";

type Props = {
    selectableCodes: CompanyReservationCode[];
    existingCompanyProducts: B2bCompanyProduct[];
    isLegacy: boolean;
    onChange: (product: B2bCompanyProduct) => void;
};

const SelectCompanyProductPopover = (props: Props) => {
    const [sourceEvents, setSourceEvents] = useState<Event[] | null>(null);
    const [sourceProducts, setSourceProducts] = useState<Product[] | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [variants, setVariants] = useState<ProductVariant[]>([]);

    const [eventId, setEventId] = useState<string>("");
    const [productId, setProductId] = useState<string>("");
    const [variantId, setVariantId] = useState<string>("");
    const [codeHolderId, setCodeHolderId] = useState<string>("");

    const api = new ApiBackend();

    useEffect(() => {
        fetchSourceProducts();
    }, []);

    const handleEventIdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEventId(e.target.value);

        const nextProducts = sourceProducts.filter(x => x.EventId === e.target.value);
        setProducts(nextProducts);
        setProductId("");
        setVariantId("");
    };

    const handleProductIdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProductId(e.target.value);

        const nextVariants = products.find(x => x.id === e.target.value).variants;

        setVariants(nextVariants);
        setVariantId("");
    };

    const handleVariantIdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVariantId(e.target.value);
    };

    const render = () => {
        if (!sourceProducts || !sourceEvents) {
            return (<div style={{ display: "block", height: 200 }}></div>);
        }

        const { isLegacy } = props;

        return isLegacy ? renderLegacy() : renderNew();
    };

    const renderLegacy = () => {
        return (
            <ProductVariantPriceGroupSelector
                headerText="Välj produkt"
                buttonText="Ok"
                products={sourceProducts}
                includePriceGroups={true}
                onChange={onChangeLegacy}
            />
        )
    };

    const renderNew = () => {
        const { selectableCodes } = props;
        const commitEnabled = !!(productId && variantId);

        return (
            <Card>
                <CardHeader title="Välj produkt" />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <TextField
                                select
                                style={{ flex: 1, margin: "0 3px" }}
                                label="Event"
                                InputLabelProps={{ shrink: true }}
                                value={eventId}
                                fullWidth
                                onChange={handleEventIdChanged}
                            >
                                {sourceEvents.map((e, idx) => {
                                    return <MenuItem key={idx} value={e.id}>{e.name}</MenuItem>;
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                select
                                style={{ flex: 1, margin: "0 3px" }}
                                label="Produkt"
                                InputLabelProps={{ shrink: true }}
                                value={productId}
                                fullWidth
                                disabled={!eventId}
                                onChange={handleProductIdChanged}
                            >
                                {products.map((p, idx) => {
                                    return <MenuItem key={idx} value={p.id}>{p.name}</MenuItem>;
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                select
                                style={{ flex: 1, margin: "0 3px" }}
                                label="Produktvariant"
                                InputLabelProps={{ shrink: true }}
                                value={variantId}
                                fullWidth
                                disabled={!productId}
                                onChange={handleVariantIdChanged}
                            >
                                {variants.map((p, idx) => {
                                    return <MenuItem key={idx} value={p.Id}>{p.Name}</MenuItem>;
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <ReservationCodeSelector
                                selectableCodes={selectableCodes}
                                productId={productId}
                                variantId={variantId}
                                onChange={(value: string) => {
                                    setCodeHolderId(value);
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions>
                    <Button
                        color="secondary"
                        disabled={!commitEnabled}
                        onClick={handleCommit}>
                        Ok
                    </Button>
                </CardActions>
            </Card>
        )
    };

    const onChangeLegacy = (res: IProductVariantPriceGroupSelectorResult) => {
        const { onChange } = props;

        const newProduct: B2bCompanyProduct = {
            id: null,
            productId: res.product.id,
            productVariantId: res.productVariant.Id,
            productVariantPriceGroupId: res.productVariantPriceGroup.id,
            name: res.product.name,
            description: res.product.description,
            isActive: false,
            b2bInvoice: false
        };

        onChange(newProduct);
    };

    const handleCommit = () => {
        const { onChange } = props;

        const productMatch = products.find(x => x.id === productId);

        const nextProduct: B2bCompanyProduct = {
            id: null,
            productId: productId,
            productVariantId: variantId,
            codeHolderId: codeHolderId,
            name: productMatch.name,
            description: productMatch.description,
            isActive: false,
            b2bInvoice: false
        };

        onChange(nextProduct);
    };

    const fetchSourceProducts = async () => {
        const { existingCompanyProducts } = props;

        const allEvents = await api.listEvents();
        const allProducts = await api.listProducts();
        const raceProducts = allProducts.filter(product => product.type === "race");

        const applicableProductsVariants: Product[] = [];
        for (let i = 0; i < raceProducts.length; i++) {
            const raceProduct = raceProducts[i];

            if (!existingCompanyProducts.some(x => x.productId === raceProduct.id)) {
                applicableProductsVariants.push(raceProduct);
                continue;
            }

            const applicableVariants: ProductVariant[] = [];
            const companyProductMatches = existingCompanyProducts.filter(x => x.productId === raceProduct.id);
            for (let j = 0; j < raceProduct.variants.length; j++) {
                const raceVariant = raceProduct.variants[j];

                if (!companyProductMatches.some(x => x.productVariantId === raceVariant.Id)) {
                    applicableVariants.push(raceVariant);
                }
            }

            if (applicableVariants.length > 0) {
                applicableProductsVariants.push({
                    ...raceProduct,
                    variants: applicableVariants
                });
            }
        }

        setSourceProducts(applicableProductsVariants.sort((p1, p2) => p1.name.localeCompare(p2.name, "sv")));

        const applicableEvents = allEvents
            .filter(e => applicableProductsVariants.some(p => p.EventId === e.id))
            .sort((a, b) => a.name.localeCompare(b.name, "sv"));

        setSourceEvents(applicableEvents);
    };

    return render();
};

export default SelectCompanyProductPopover;
